import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OrganizationsQueryKeys, useOrganizationsApi } from '@/api';
import { OrganizationCreate, UpdateOrganizationRequest } from '@/api/api-client';
import { mapOrganizations } from '@/api/api-mappers';
import { GetOrganizationsRequest } from '@/api/api-queries/duck';

export const useGetOrganizations = (request: GetOrganizationsRequest) => {
  const { organizationsApi } = useOrganizationsApi();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [OrganizationsQueryKeys.organizationsList, request],
    queryFn: async () => {
      const response = await organizationsApi.getOrganizationsApiOrganizationsGet(
        request?.sortBy,
        request?.sortOrder,
        request?.page ?? 1,
        request?.pageSize,
        request?.name,
        request?.domain,
        null,
      );

      return response.data;
    }
  });

  const organizations = data ? mapOrganizations(data.data) : [];

  return {
    organizations,
    isOrganizationsLoading: isLoading,
    isOrganizationsError: isError,
    refetchOrganizations: refetch,
    totalPages: data?.meta.totalPages ?? 1,
    totalItems: data?.meta.totalItems ?? 1,
  };
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();
  const { organizationsApi } = useOrganizationsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (request: OrganizationCreate) => organizationsApi.createOrganizationApiOrganizationsPost(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [OrganizationsQueryKeys.organizationsList],
      });
    },
  });

  return {
    createOrganization: mutateAsync,
    isCreating: isPending,
    isError,
    error,
  };
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const { organizationsApi } = useOrganizationsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (request: {
      id: string,
      data: UpdateOrganizationRequest
    }) => organizationsApi.updateOrganizationApiOrganizationsOrganizationIdPut(request.id, request.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [OrganizationsQueryKeys.organizationsList],
      });
    },
  });

  return {
    updateOrganization: mutateAsync,
    isUpdating: isPending,
    isError,
    error,
  };
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();
  const { organizationsApi } = useOrganizationsApi();

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: (id: string) => organizationsApi.deleteOrganizationApiOrganizationsOrganizationIdDelete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [OrganizationsQueryKeys.organizationsList],
      });
    },
  });

  return {
    deleteOrganization: mutateAsync,
    isDeleting: isPending,
    isError,
    error,
  };
};